// ---------------------------------------------------------------------------
// UnitsPicker — miles, or kilometres, and which short unit goes with them.
//
// SHOWS THE ANSWER, NOT THE CATEGORY. "Imperial" and "Metric" are words people
// read past; "3.4 mi · 500 ft" is the thing they are actually choosing between,
// and it is instantly obvious which one is theirs. The examples are formatted
// by the real formatter, not typed in — so a change to fmtDistance shows up
// here rather than leaving this screen quietly lying about what the app does.
//
// THREE OPTIONS, BECAUSE BRITAIN IS NOT A ROUNDING ERROR. Morgan, Aug 18:
// "miles and yards, like the road signs". UK roads are signed in miles and
// yards while the rest of the country is metric, so folding it into either of
// the other two produces something a British driver reads as broken.
// ---------------------------------------------------------------------------

import { Check } from "lucide-react";
import { useUnitSystem } from "@/hooks/useUnitSystem";
import { UNIT_SYSTEMS, UNIT_LABELS, type UnitSystem } from "@/utils/unitSettings";
import { fmtDistance } from "@/utils/units";

/** The two distances that actually distinguish the systems: one long enough to
 *  need the big unit, one short enough to need the small one. Rendered through
 *  fmtDistance so this preview cannot drift from the app. */
const LONG_M = 5500;
const SHORT_M = 150;

function rowClass(selected: boolean): string {
  return `w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-smooth ${
    selected
      ? "border-primary/60 bg-pleasant/10"
      : "border-border bg-card hover:bg-muted/50"
  }`;
}

export function UnitsPicker() {
  const [system, setSystem] = useUnitSystem();

  return (
    <div className="space-y-2">
      {UNIT_SYSTEMS.map((id: UnitSystem) => {
        const selected = id === system;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setSystem(id)}
            className={rowClass(selected)}
            aria-pressed={selected}
          >
            <span className="min-w-0 text-left">
              <span className="block text-sm font-medium text-foreground">
                {UNIT_LABELS[id].label}
              </span>
              <span className="block text-xs text-muted-foreground tabular-nums">
                {fmtDistance(LONG_M, id)} · {fmtDistance(SHORT_M, id)}
              </span>
            </span>
            {selected && <Check className="w-4 h-4 text-primary shrink-0" />}
          </button>
        );
      })}
      <p className="text-xs text-muted-foreground pt-1">
        Changes distances everywhere — route cards, directions, and the voice
        that reads your turns aloud.
      </p>
    </div>
  );
}
